/* eslint-disable no-underscore-dangle */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from '../Chart/Chart';
import ItemLists from '../ItemLists/ItemLists';
import Navbar from '../Navbar/Navbar';
import ProgressBar from '../ProgressBar/ProgressBar';
import Sidebar from '../Sidebar/Sidebar';
import OrderWorkflow from '../OrderWorkflow/OrderWorkflow';
import LandingPopup from '../LandingPopup/LandingPopup';
import { logoutUser, ordersAPI, weeklyReportAPI } from '../../services/apiService';
import './Home.css';

function Home() {
    const [showPopup, setShowPopup] = useState(false);
    const [orders, setOrders] = useState([]);
    const [weeklyReport, setWeeklyReport] = useState({});
    const isStaff = JSON.parse(localStorage.getItem('is_staff'));

    const { t, i18n } = useTranslation();

    useEffect(() => {
        const group = localStorage.getItem('userGroup');
        if (!group) {
            setShowPopup(true);
        }
    }, []);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const response = await ordersAPI.getAll();
                const data = response?.data ?? response;

                if (Array.isArray(data)) {
                    setOrders(data);
                } else if (Array.isArray(data?.results)) {
                    setOrders(data.results);
                } else {
                    setOrders([]);
                }
            } catch (error) {
                console.error('Failed to fetch orders:', error);
                setOrders([]);
            }
        };

        fetchOrders();
    }, []);

    useEffect(() => {
        const fetchWeeklyReport = async () => {
            try {
                const response = await weeklyReportAPI.getAll();
                const data = response?.data ?? response;
                if (data && typeof data === 'object') {
                    setWeeklyReport(data);
                } else {
                    setWeeklyReport({});
                }
            } catch (error) {
                console.error('Failed to fetch WeeklyReport:', error);
                setWeeklyReport([]);
            }
        };

        fetchWeeklyReport();
    }, []);

    const statusCounts = useMemo(
        () =>
            orders.reduce(
                (acc, order) => {
                    const status = String(order?.status || '').toLowerCase();

                    if (acc[status] !== undefined) {
                        acc[status] += 1;
                    }

                    return acc;
                },
                {
                    pending: 0,
                    preparing: 0,
                    served: 0,
                    paid: 0,
                    cancelled: 0,
                }
            ),
        [orders]
    );
    const totalOrdersCount = useMemo(
        () => orders.filter((order) => String(order?.status || '').toLowerCase() === 'paid').length,
        [orders]
    );

    const getOrderFinalPrice = (order) => {
        const total = Number(order?.total_price || 0);
        return total;
    };

    const totalOrdersPrice = useMemo(
        () =>
            orders
                .filter((order) => String(order?.status || '').toLowerCase() === 'paid')
                .reduce((total, order) => total + getOrderFinalPrice(order), 0),
        [orders]
    );

    const preparingCount = statusCounts.preparing || 0;
    const servedCount = statusCounts.served || 0;
    const paidCount = statusCounts.paid || 0;
    const cancelledCount = statusCounts.cancelled || 0;

    const formatPrice = (value) =>
        Number(value || 0).toLocaleString(i18n.language === 'fa' ? 'fa-IR' : 'en-US');

    const handleLogout = async () => {
        try {
            await logoutUser(true);
        } catch (err) {
            console.error('Logout failed:', err);
            window.location.href = '/login';
        }
    };

    return (
        <div className="home">
            <Sidebar />

            <div className="home_main">
                <Navbar />

                <div className="bg_color" />

                <div className="home_items">
                    <ItemLists type="user" count={preparingCount} />
                    <ItemLists type="orders" count={servedCount} />
                    <ItemLists type="products" count={paidCount} />
                    <ItemLists type="balance" count={cancelledCount} />
                </div>
                <OrderWorkflow orders={orders} setOrders={setOrders} isStaff={isStaff} />
                <div className="chart_sec">
                    <div className="chart_sidebar_controls">
                        <LandingPopup />
                        <ProgressBar
                            totalOrdersCount={totalOrdersCount}
                            totalOrdersPrice={totalOrdersPrice}
                        />
                    </div>
                    <Chart height={840} title={t('income')} weeklyReport={weeklyReport} />
                </div>
            </div>
            {showPopup && (
                <div className="popup_overlay">
                    <div className="popup_box">
                        <p>{t('no_group_message')}</p>
                        <button type="button" onClick={handleLogout}>
                            {t('logout')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Home;
